import { loadDeployment, validateAssistant, loadDraft } from '@/api';
import { createRule, getRule, setRule, removeRule } from '@/rules';
import { setCookie, getCookie, schedule } from '@/utils';
import { setConfig, getConfig, getDebugEnabled, setDebugEnabled, getDebugVersion } from '@/config';
import { createGlobalListenerStore } from '@/listeners';

const globalListenerStore = createGlobalListenerStore({
	onLocationChange: () => initDeployments(),
});

function handleListenerStore() {
	globalListenerStore.clearListeners(true);
	if (Object.keys(getConfig(config => config.deployments)).length > 0) {
		globalListenerStore.setListeners();
	}
}

function isValidAssistantTokenFormat(token) {
	if (typeof token !== 'string') {
		return false;
	}

	if (token.length === 0) {
		return false;
	}

	return /^[a-zA-Z0-9]+$/.test(token);
}

function handleAssistant(id) {
	if (getDebugEnabled(id)) {
		return;
	}

	try {
		const search = window.location.href.split('?')[1];
		const urlParams = new URLSearchParams(search);
		const assistantToken = urlParams.get('mopinion-assistant') || getCookie('MOP_assistant');
		if (isValidAssistantTokenFormat(assistantToken)) {
			console.log('validating assistant token', assistantToken);
			validateAssistant(id, {
				token: assistantToken,
				onSuccess: response => {
					if (response.valid) {
						setCookie('MOP_assistant', assistantToken);
						setDebugEnabled(id, true);
						import('@/assistant').then(assistant => {
							assistant.start();
						});
						load(id);
					}
				},
			});
		}
	} catch (e) {}
}

export function load(id = '', customConfig = {}) {
	const { refresh } = customConfig;

	setConfig(config => ({
		...config,
		...customConfig,
		refresh,
		debug: config.debug,
	}));

	handleAssistant(id);

	const onSuccess = response => {
		if (!response.OK) {
			console.log(`status of ${id} not OK!`);
			return;
		}

		if (response.deployment) {
			if (refresh) {
				abortOne(id);
			}
			setConfig(config => {
				return {
					...config,
					mopinion: {
						...(config.mopinion ?? {}),
						...(config.survey ?? {}),
						...(response.global ?? {}),
						...(response.deployment.use_collect && {
							use_collect: response.deployment.use_collect,
						}),
						...(customConfig.mopinion ?? {}),
					},
					deployments: {
						...config.deployments,
						[id]: response.deployment,
					},
				};
			});
		}

		initDeployments();
	};

	if (getDebugEnabled(id) && getDebugVersion(id) === 'draft') {
		console.log('Loading draft version...');
		loadDraft(id, { onSuccess });
		return;
	}

	loadDeployment(id, { onSuccess });
}

export function initDeployments() {
	handleListenerStore();
	Object.keys(getConfig(config => config.deployments)).forEach(deploymentKey => {
		const deployment = getConfig(config => config.deployments[deploymentKey]);
		console.log('checking deployment', deployment.id);
		initRules(deployment);
	});
}

function initRules({ published, id, rules } = {}) {
	if (!published) {
		console.log(`${id} is not published`);
		return;
	}

	rules?.forEach(rule => {
		schedule(() => {
			if (!getRule(rules => rules[rule.id])) {
				setRule(rules => {
					rules[rule.id] = createRule({
						rule,
						globalListenerStore,
					});
				});
			}
			getRule(rules => rules[rule.id])?.checkConditions();
		});
	});
}

export function abort(deploymentKey) {
	if (deploymentKey) {
		return abortOne(deploymentKey);
	}

	Object.keys(getConfig(config => config.deployments)).forEach(deploymentKey => {
		abortOne(deploymentKey);
	});
}

function abortOne(deploymentKey) {
	const deployment = getConfig(config => config.deployments?.[deploymentKey]);
	if (deployment) {
		console.log('removing deployment', deployment.id);
		deployment.rules?.forEach(rule => {
			removeRule(rule.id);
		});
		setConfig(config => {
			delete config.deployments[deploymentKey];
		});
	}

	handleListenerStore();
}

export function getActive() {
	return getConfig(config => config.deployments);
}
